import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, FileText, Loader2 } from "lucide-react";
import { DealsFilter, DealFilters } from "@/components/deals/DealsFilter";

const AthleteDeals = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<DealFilters>({});
  const [athleteId, setAthleteId] = useState<string | null>(null);

  useEffect(() => {
    const checkAthleteProfile = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate("/auth");
        return;
      }

      const { data: athleteProfile } = await supabase
        .from("athlete_profiles")
        .select("athlete_id")
        .eq("user_id", user.id)
        .single();

      if (athleteProfile) {
        setAthleteId(athleteProfile.athlete_id);
      } else {
        navigate("/dashboard");
      }
    };

    checkAthleteProfile();
  }, [navigate]);

  const { data: deals, isLoading } = useQuery({
    queryKey: ["athlete-deals", athleteId],
    queryFn: async () => {
      if (!athleteId) return [];
      const { data, error } = await supabase
        .from("deals")
        .select("*")
        .eq("athlete_id", athleteId)
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      return data;
    },
    enabled: !!athleteId,
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    const colors = {
      draft: "secondary",
      pending_compliance: "default",
      active: "default",
      rejected: "destructive",
      cancelled: "secondary",
    };
    return colors[status as keyof typeof colors] || "secondary";
  };

  const filteredDeals = useMemo(() => {
    if (!deals) return [];
    
    return deals.filter((deal) => {
      if (filters.status && deal.status !== filters.status) return false;
      if (filters.dealType && deal.deal_type !== filters.dealType) return false;
      if (filters.riskLevel && deal.risk_level !== filters.riskLevel) return false;
      if (filters.minAmount && deal.amount < filters.minAmount) return false;
      if (filters.maxAmount && deal.amount > filters.maxAmount) return false;
      if (filters.brandName && !deal.brand_name.toLowerCase().includes(filters.brandName.toLowerCase())) return false;
      if (filters.startDateFrom && new Date(deal.start_date) < new Date(filters.startDateFrom)) return false;
      if (filters.startDateTo && new Date(deal.start_date) > new Date(filters.startDateTo)) return false;
      
      return true;
    });
  }, [deals, filters]);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">My Deals</h1>
            <p className="text-muted-foreground mt-2">
              View and manage all your NIL deals
            </p>
          </div>
          <Button onClick={() => navigate("/athlete/deals/submit")}>
            <Plus className="mr-2 h-4 w-4" />
            Submit New Deal
          </Button>
        </div>

        <DealsFilter filters={filters} onFiltersChange={setFilters} />

        {isLoading ? (
          <div className="flex items-center justify-center h-96">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : filteredDeals.length > 0 ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Showing {filteredDeals.length} of {deals?.length || 0} deals
            </p>
            {filteredDeals.map((deal) => (
              <Card 
                key={deal.id}
                className="cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => navigate(`/athlete/deals/${deal.id}`)}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-xl">{deal.title}</CardTitle>
                      <p className="text-sm text-muted-foreground mt-1">
                        {deal.brand_name}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <Badge variant={getStatusColor(deal.status) as any}>
                        {deal.status.replace(/_/g, " ")}
                      </Badge>
                      <span className="text-lg font-bold">
                        {formatCurrency(deal.amount)}
                      </span>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Type</p>
                      <p className="font-medium capitalize">
                        {deal.deal_type.replace(/_/g, " ")}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Start Date</p>
                      <p className="font-medium">
                        {new Date(deal.start_date).toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">End Date</p>
                      <p className="font-medium">
                        {deal.end_date
                          ? new Date(deal.end_date).toLocaleDateString()
                          : "Ongoing"}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Submitted</p>
                      <p className="font-medium">
                        {new Date(deal.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  {deal.description && (
                    <p className="mt-4 text-sm text-muted-foreground">
                      {deal.description}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="text-center py-12">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">
                No deals found
              </p>
              <Button onClick={() => navigate("/athlete/deals/submit")}>
                <Plus className="mr-2 h-4 w-4" />
                Submit Your First Deal
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AthleteDeals;
